import { safeDelay, SafeError, SafePromise, SafeType } from '@benzinga/safe-await';
import {
  NotificationHistoryResponse,
  NotificationHistoryParams,
  ReadStatus,
  NotificationMarkReadUnreadParams,
  MarkReadUnreadParams,
  NotificationReadUpdateParams,
  NotificationConfigGetResponse,
  NotificationConfigSetResponse,
  NotificationConfigParams,
  MarketNotificationFrequency,
  MarketNotificationFrequencyRes,
  GetMarketNotificationFrequencyResponse,
  UpdateGlobalNotificationSettings,
  FetchGlobalNotificationSettings,
  UpdateGlobalNotificationSettingsParams,
  EditorialMarketAlertsNotificationParams,
  SendGroupDataResponse,
  EditorialMarketAlertsNotificationResponse,
  EditorialMarketAlertsAnalyticsResponse,
  NotificationRegisterParams,
  NotificationRegisterResponse,
  RegisterTokenParams,
} from './entities';
import { NotificationRestful } from './restful';
import { Session } from '@benzinga/session';
import { ListenableSubscribable } from '@benzinga/subscribable';

interface NotificationsHistoryErrorEvent {
  error: SafeError;
  type: 'notifications:get_history_error';
}

interface NotificationsMarkReadUnreadErrorEvent {
  error: SafeError;
  type: 'notifications:mark_read_unread_error';
}

export interface NotificationsHistoryEvent {
  type: 'notifications:get_history';
  limit: number;
  data: NotificationHistoryResponse['data'];
  status: ReadStatus;
}

export interface NotificationsMarkReadUnreadEvent extends MarkReadUnreadParams {
  type: 'notifications:mark_read_unread';
}

export type NotificationRestfulEvent =
  | NotificationsMarkReadUnreadEvent
  | NotificationsHistoryErrorEvent
  | NotificationsHistoryEvent
  | NotificationsMarkReadUnreadErrorEvent;

export class NotificationRequest extends ListenableSubscribable<NotificationRestfulEvent> {
  private restful: NotificationRestful;

  constructor(session: Session) {
    super();
    this.restful = new NotificationRestful(session);
  }

  public registerToken = async (params: RegisterTokenParams) => {
    const response = await this.restful.registerToken(params);
    return response;
  };

  public getNotificationHistory = async (
    options: NotificationHistoryParams,
  ): SafePromise<NotificationHistoryResponse | undefined> => {
    let response = {} as SafeType<NotificationHistoryResponse, string, unknown>;
    let i = 0;
    do {
      response = await this.restful.getNotificationHistory(options);
      i++;
      if (response.ok?.ok) {
        this.dispatch({
          data: response.ok?.data,
          limit: options.limit,
          status: options.read_status,
          type: 'notifications:get_history',
        });
      } else {
        await safeDelay(2000);
      }
    } while (!response.ok?.ok && i < 3);

    if (response.err) {
      this.dispatch({ error: response.err, type: 'notifications:get_history_error' });
    }

    return response;
  };

  public queryNotificationHistory = (
    options: NotificationHistoryParams,
  ): SafePromise<NotificationHistoryResponse> => {
    return this.restful.getNotificationHistory(options);
  };

  public getConfigurations = async (): SafePromise<NotificationConfigGetResponse> => {
    const response = await this.restful.getConfigurations();
    return response;
  };

  public setConfigurations = async (
    options: NotificationConfigParams,
  ): SafePromise<NotificationConfigSetResponse | undefined> => {
    const response = await this.restful.setConfigurations(options);
    return response;
  };

  public getMarketNotificationFrequency = async (): SafePromise<GetMarketNotificationFrequencyResponse> => {
    const response = await this.restful.getMarketNotificationFrequency();
    return response;
  };

  public setMarketNotificationFrequency = async (
    options: MarketNotificationFrequency,
  ): SafePromise<MarketNotificationFrequencyRes | undefined> => {
    const response = await this.restful.setMarketNotificationFrequency(options);
    return response;
  };

  public fetchGlobalNotificationSettings = async (): SafePromise<FetchGlobalNotificationSettings> => {
    const response = await this.restful.fetchGlobalNotificationSettings();
    return response;
  };

  public updateGlobalNotificationSettings = async (
    params: UpdateGlobalNotificationSettingsParams,
  ): SafePromise<UpdateGlobalNotificationSettings> => {
    const response = await this.restful.updateGlobalNotificationSettings(params);
    return response;
  };

  public sendEditorialMarketAlertsNotification = async (
    params: EditorialMarketAlertsNotificationParams,
  ): SafePromise<EditorialMarketAlertsNotificationResponse> => {
    const response = await this.restful.sendEditorialMarketAlertsNotification(params);
    return response;
  };

  public getMarketAlertsAnalytics = async (): SafePromise<EditorialMarketAlertsAnalyticsResponse> => {
    const response = await this.restful.getMarketAlertsAnalytics();
    return response;
  };

  public getSendGroupData = async (): SafePromise<SendGroupDataResponse> => {
    const response = await this.restful.getSendGroupData();
    return response;
  };

  public markReadUnread = async (params: NotificationMarkReadUnreadParams): Promise<void> => {
    // update optimistically
    this.dispatch({
      readAll: params.read_all,
      readIds: params.read_ids,
      type: 'notifications:mark_read_unread',
      unreadIds: params.unread_ids,
    });

    let err: SafeError<string, unknown> | undefined | null = null;
    let i = 0;
    while (err !== undefined && i < 10) {
      const response = await this.restful.markReadUnread(params);
      err = response.err;
      if (err) {
        this.dispatch({ error: err, type: 'notifications:mark_read_unread_error' });
        await safeDelay(2000);
        i++;
      }
    }
  };

  public updateNotificationRead = async (
    options: NotificationReadUpdateParams,
  ): SafePromise<NotificationHistoryResponse | undefined> => {
    return this.restful.updateNotificationRead(options);
  };

  public registerPushDevice = async (params: NotificationRegisterParams): SafePromise<NotificationRegisterResponse> => {
    return this.restful.registerPushDevice(params);
  };

  public unregisterPushDevice = async (params: {
    push: PushSubscription;
  }): SafePromise<NotificationRegisterResponse> => {
    return this.restful.unregisterPushDevice(params);
  };
}
